<template>
  <app-shell breadcrumbs="Home,Payment Options">
    <div class="payment-options-manager">
      <!-- Tip -->
      <ml-tip>
        <div slot="title" class="ml-tip__title">
          <img src="/static/img/icons/idea.png" alt="Idea">

          Helpful Tip
        </div>

        <div slot="content">
          A payment mode is the type of experience you choose to offer to your users on checkout.
          There are 4 payment mode currently available on checkout:-
          STK push, USSD push, instructional and card.
          <b>STK push</b> is when the user's sim toolkit is launched and he/she is prompted to authorize the payment,
          while <b>USSD push</b> is when a user is prompted to authorize the payment via USSD,
          <b>Instructional</b> mode is when instructions are shown to the user on how to pay on checkout and finally
          <b>Card</b> is for a user to to insert the card details to checkout.
        </div>
      </ml-tip>
      <!-- End of the payment options tip -->

      <ml-group title="Payment options"
                subtitle="Select the payment options for your new services"
                class="mt-4"
      >
        <template slot="legend">
          <!-- Payment option legend -->
          <div class="ml-pom__legend">
            <ul class="ml-pom__legend-list">
              <li class="ml-pom__legend-list__item">
                <div class="ml-pom__check-icon mr-2 blue">
                  <div class="ml-pom__check-icon__center"></div>
                </div>

                <span>Active</span>
              </li>
              <li class="ml-pom__legend-list__item">
                <div class="ml-pom__check-icon mr-2 orange">
                  <div class="ml-pom__check-icon__center"></div>
                </div>

                <span>Syncing</span>
              </li>
              <li class="ml-pom__legend-list__item">
                <div class="ml-pom__check-icon mr-2">
                  <div class="ml-pom__check-icon__center"></div>
                </div>

                <span>Inactive</span>
              </li>
            </ul>
          </div>
          <!-- End of payment option legend -->
        </template>
        <template slot="content">
          <!-- Payment options grouped by country -->
          <div class="ml-pol">
            <div v-for="(country, index) in options" :key="`${country.title.replace(/\s/g,'')}#${index}`" class="ml-pol__item">
              <div class="ml-pol__item__title">
                {{ country.title }}
              </div>

              <ul class="ml-pol__item-list">
                <li v-for="(option, index) in country.options" :key="`${option.clientDisplayName.replace(/\s/g,'')}#${index}`" class="ml-pol__item-list__item">
                  <payment-option-manager
                    :image-source="`${logoUrls['KE']}/${option.clientLogo}`"
                    :selected="option.optionEnabled"
                    :status="option.serviceSettingActive"
                    :title="option.clientDisplayName"
                    :payment-option="option"
                    @option-activated="addPaymentOption"
                    @option-deactivated="removePaymentOption"
                    @option-updated="updatePaymentOption"
                  ></payment-option-manager>
                </li>
              </ul>
            </div>
          </div>
          <!-- End of the payment options list -->
        </template>
      </ml-group>
    </div>
  </app-shell>
</template>

<script>
  import _ from 'lodash'
  import { mapGetters } from 'vuex'
  import Config from '@/api/config'
  import Tip from '@/components/Tip'
  import Group from '@/components/Group'
  import Shell from '@/components/Shell'
  import Service from '@/api/services/service'
  import PaymentOptions from '@/api/services/paymentOption'
  import PaymentOptionManager from '@/components/PaymentOptionManager'

  const logInfo = !!(process.env.NODE_ENV !== 'production')

  export default {
    components: {
      'app-shell': Shell,
      'ml-group': Group,
      'ml-tip': Tip,
      'payment-option-manager': PaymentOptionManager
    },
    computed: {
      ...mapGetters('services', ['currentService', 'allServices']),
      ...mapGetters('user', ['clientInfo']),
      logoUrls () {
        return Config.LOGO_URLS
      }
    },
    data () {
      return {
        options: []
      }
    },
    methods: {
      addPaymentOption (option) {
        logInfo && console.log(
          'Add the payment option =>',
          _.pick(option, ['countryPayerClientID', 'clientDisplayName', 'selectedPaymentModeID', 'countryName'])
        )

        Service.update({
          payload: {
            'serviceCode': this.currentService.serviceCode,
            'serviceName': this.currentService.serviceName,
            'clientCode': this.clientInfo.client_code,
            'options': [_.merge(option, { optionEnabled: true, serviceSettingActive: 2 })]
          }
        }).then(response => {
          logInfo && console.log('Creation of the service setting was a success. Response =>', response)
          // Search for the option in the array of options and update the various fields
          // [optionEnabled, active]
          option.optionEnabled = true
          this.$snackbar.open({
            message: `${option.clientDisplayName} is added`,
            type: 'is-success',
            position: 'is-bottom',
            queue: false,
            actionText: 'UNDO',
            onAction: () => {
              this.removePaymentOption(option)
            }
          })
        }).catch(reason => {
          logInfo && console.log('Creation of the service setting failed. Reason =>', reason)
          this.$snackbar.open({
            message: `Adding ${option.clientDisplayName} to failed`,
            type: 'is-error',
            position: 'is-bottom',
            queue: false,
            actionText: 'RETRY',
            onAction: () => {
              this.addPaymentOption(option)
            }
          })
        })
      },
      removePaymentOption (option) {
        logInfo && console.log(
          'Remove the payment option =>',
          _.pick(option, ['countryPayerClientID', 'clientDisplayName', 'selectedPaymentModeID', 'countryName'])
        )

        Service.update({
          payload: {
            'serviceCode': this.currentService.serviceCode,
            'serviceName': this.currentService.serviceName,
            'clientCode': this.clientInfo.client_code,
            'options': [_.merge(option, { optionEnabled: false })]
          }
        }).then(response => {
          logInfo && console.log('Removal of the service setting was a success. Response =>', response)
          // Search for the option in the array of options and update the various fields
          // [optionEnabled, active]
          option.optionEnabled = false
          this.$snackbar.open({
            message: `${option.clientDisplayName} is removed`,
            type: 'is-success',
            position: 'is-bottom',
            queue: false,
            actionText: 'UNDO',
            onAction: () => {
              this.addPaymentOption(option)
            }
          })
        }).catch(reason => {
          logInfo && console.log('Removal of the service setting failed. Reason =>', reason)
          this.$snackbar.open({
            message: `Removing ${option.clientDisplayName} from ${this.currentService.serviceName} failed`,
            type: 'is-error',
            position: 'is-bottom',
            queue: false,
            actionText: 'RETRY',
            onAction: () => {
              this.removePaymentOption(option)
            }
          })
        })
      },
      updatePaymentOption (option) {
        logInfo && console.log(
          'Update the payment option =>',
          _.pick(option, ['countryPayerClientID', 'clientDisplayName', 'selectedPaymentModeID', 'countryName'])
        )

        Service.update({
          payload: {
            'serviceCode': this.currentService.serviceCode,
            'serviceName': this.currentService.serviceName,
            'clientCode': this.clientInfo.client_code,
            'options': [option]
          }
        }).then(response => {
          logInfo && console.log('Update of the service setting was a success. Response =>', response)
          this.$snackbar.open({
            message: `${option.clientDisplayName} successfully updated`,
            type: 'is-success',
            position: 'is-bottom',
            queue: false
          })
        }).catch(reason => {
          logInfo && console.log('Updating of the service setting failed. Reason =>', reason)
          this.$snackbar.open({
            message: `${option.clientDisplayName} failed to update`,
            type: 'is-error',
            position: 'is-bottom',
            queue: false
          })
        })
      }
    },
    mounted () {
      PaymentOptions.forService({ serviceCode: this.currentService.serviceCode })
        .then(paymentOptions => {
          logInfo && console.log('Successfully fetched the payment options: ', paymentOptions)
          this.options = paymentOptions
        })
        .catch(reason => {
          logInfo && console.log('Failed to fetch the payment options: => ', reason)
        })
    },
    name: 'PaymentOptions'
  }
</script>

<style lang="scss">
  @import "../styles/theme";

  /* Payment options legend */
</style>



// WEBPACK FOOTER //
// src/views/PaymentOptions.vue